import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';

/*
 * The 2026 mark's geometry (sbg-icon-digital.svg): two Blue blocks forming the
 * stepped S and the cyan Spark riding up and to the right. Used only for the
 * quiet empty-state mark and the boot loader (book §8.1, §11.15) — never
 * recolored as a logo. The logo itself comes from assets/brand.
 */
const SPARK =
  'M192.105 5.40541L159.852 61.2973C155.739 68.4324 160.934 77.4054 169.16 77.4054H233.774C237.562 77.4054 241.133 75.3514 243.082 72L275.334 16.1081C279.447 8.97295 274.252 0 266.026 0H201.413C197.625 0 194.053 2.05405 192.105 5.40541Z';
const BLOCKS =
  'M106.851 97.8366L148.628 25.5123C152.091 19.4582 147.762 11.8906 140.727 11.8906H53.0599C49.813 11.8906 46.7825 13.6204 45.1591 16.4312L1.21742 92.3231C-2.24596 98.3772 2.08327 105.945 9.11826 105.945H92.7806C97.0016 105.945 99.5991 110.485 97.4345 114.053L55.6575 186.377C52.1941 192.431 56.5233 199.999 63.5583 199.999H151.333C154.58 199.999 157.611 198.269 159.234 195.458L203.068 119.566C206.531 113.512 202.202 105.945 195.167 105.945H111.504C107.284 105.945 104.686 101.404 106.851 97.8366Z';

/** The quiet empty-state mark: Blue 100 blocks, Cyan 300 spark (both step down in dark). */
export const EmptyMark: React.FC<{ size?: number; sx?: SxProps<Theme> }> = ({ size = 48, sx }) => (
  <Box
    component="svg"
    viewBox="0 0 277 200"
    aria-hidden="true"
    sx={{ width: (size * 277) / 200, height: size, display: 'block', flex: 'none', ...sx }}
  >
    <path d={BLOCKS} fill={sbg.bg.emptyMark} />
    <path d={SPARK} fill={sbg.bg.emptySpark} />
  </Box>
);

/**
 * The brand loader: the blocks assemble along the 60° axis, then the Spark
 * lands, on a 1.2s loop. Only for app boot and auth hand-offs longer than a
 * second (book §11.15); content uses skeletons.
 */
export const BrandLoader: React.FC<{ label?: string; size?: number }> = ({ label = 'Loading', size = 48 }) => (
  <Box role="status" aria-live="polite" sx={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
    <Box
      component="svg"
      viewBox="0 0 277 200"
      aria-hidden="true"
      sx={{
        width: (size * 277) / 200,
        height: size,
        overflow: 'visible',
        '& path': { animation: `sbg-assemble 1.2s ${sbg.ease.enter} infinite both` },
        '& path:last-of-type': { animationDelay: '0.18s' },
        '@media (prefers-reduced-motion: reduce)': { '& path': { animation: 'none' } },
      }}
    >
      <path d={BLOCKS} fill={sbg.action.primary} />
      <path d={SPARK} fill="var(--sbg-spark)" />
    </Box>
    <span className="sbg-sr-only">{label}</span>
  </Box>
);
